import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Alert,
  Spinner,
  Form,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faUser,
  faCheckCircle,
  faSignInAlt,
} from "@fortawesome/free-solid-svg-icons";
import { GoogleLogin } from "@react-oauth/google";
import apiClient from "../utils/apiClient";
import {
  validateGoogleToken,
} from "../utils/googleTokenUtils";
import { jwtDecode } from 'jwt-decode';
import {
  sanitizeRequestData,
  isValidEventId,
} from "../utils/requestValidator";

const AttendancePage = () => {
  const { eventId } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get("token");

  const [loading, setLoading] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);

  const [error, setError] = useState(null);
  const [step, setStep] = useState("login"); // 'login', 'confirm', 'form', 'success'

  const [userEmail, setUserEmail] = useState("");

  const [existingUserData, setExistingUserData] = useState(null);
  const [guestInfo, setGuestInfo] = useState({
    name: "",
    email: "",
    document: "",
    phone: "",
    organization: "",
  });

  useEffect(() => {
    if (!eventId || eventId === "undefined") {
      setError("ID de evento inválido en la URL");
      return;
    }

    if (!token || token === "undefined") {
      setError("Token de evento inválido en la URL");
      return;
    }

    const loadEventInfo = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(
          `/attendance/${eventId}/info/?token=${token}`
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const now = new Date();
            const eventEnd = new Date(data.event.end);
            const expiration = new Date(eventEnd.getTime() + (20 * 24 * 60 * 60 * 1000));
            
            if (now > expiration) {
              setError(`⏰ El registro de asistencia ha caducado.\n\nEl período de registro venció el ${expiration.toLocaleDateString('es-ES')}.\n\nContacta al organizador si necesitas registrar tu asistencia.`);
              return;
            }
            
            const eventTitle = data.event.title || data.event.summary || "Evento sin título";
            
            setEventInfo({
              id: eventId,
              title: eventTitle,
              date: new Date(data.event.start).toLocaleDateString("es-ES"),
              time: new Date(data.event.start).toLocaleTimeString("es-ES", {
                hour: "2-digit",
                minute: "2-digit",
              }),
              location: data.event.location || "Ubicación no especificada",
              description: data.event.description || "",
              start: data.event.start,
              end: data.event.end
            });
          } else {
            if (data.message && data.message.includes("no ha comenzado")) {
              setError(null);
              setEventInfo({
                id: eventId,
                title: "Evento",
                date: "Por confirmar",
                time: "Por confirmar",
                location: "Por confirmar",
                description: "",
                start: null,
                end: null
              });
            } else {
              setError(data.message || "Error al cargar información del evento");
            }
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 410) {
            setError("⏰ Este enlace de registro ha caducado.\n\nEl período de registro para este evento ha finalizado.");
          } else if (response.status === 425) {
            setError(null);
            setEventInfo({
              id: eventId,
              title: "Evento",
              date: "Por confirmar",
              time: "Por confirmar",
              location: "Por confirmar",
              description: "",
              start: null,
              end: null
            });
          } else {
            setError(errorData.message || "Error de conexión al obtener información del evento");
          }
        }
      } catch (err) {
        setError("Error al cargar información del evento: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    if (eventId && token) {
      loadEventInfo();
    } else {
      setError("Link inválido: faltan parámetros requeridos");
    }
  }, [eventId, token]);

  const validateEventTimingForRegistration = (eventData) => {
    const now = new Date();
    const eventStart = new Date(eventData.start);
    const eventEnd = new Date(eventData.end);
    const expiration = new Date(eventEnd.getTime() + (20 * 24 * 60 * 60 * 1000));
    
    if (now < eventStart) {
      return {
        valid: false,
        message: `⏰ El evento aún no ha comenzado.\n\nEl evento iniciará el ${eventStart.toLocaleDateString('es-ES')} a las ${eventStart.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}.\n\nPodrás registrar tu asistencia cuando el evento haya comenzado.`
      };
    }
    
    if (now > expiration) {
      return {
        valid: false,
        message: `⏰ El registro de asistencia ha caducado.\n\nEl período de registro venció el ${expiration.toLocaleDateString('es-ES')}.\n\nContacta al organizador si necesitas registrar tu asistencia.`
      };
    }
    
    return { valid: true };
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGuestInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setLoading(true);
    setError(null);

    try {
      const googleTokenReceived = credentialResponse.credential;

      if (!validateGoogleToken(googleTokenReceived)) {
        throw new Error("Token de Google inválido: formato incorrecto");
      }



      let email;
      try {
        console.log("🔍 Debug Token:", {
          tokenLength: googleTokenReceived.length,
          tokenParts: googleTokenReceived.split(".").length,
          tokenPreview: googleTokenReceived.substring(0, 50) + "...",
        });

        // Decodificar JWT usando jwt-decode
        const payload = jwtDecode(googleTokenReceived);

        console.log("🔍 Debug Payload:", {
          hasEmail: !!payload.email,
          email: payload.email,
          allKeys: Object.keys(payload),
          payloadPreview: JSON.stringify(payload).substring(0, 200) + "...",
        });

        email = payload.email;
        if (!email) {
          console.error("❌ Email vacío en payload:", payload);
          throw new Error("Email no encontrado en el token");
        }

        console.log("✅ Email extraído:", email);
      } catch (decodeError) {
        console.error("❌ Error decodificando token:", decodeError);
        console.error(
          "❌ Token problemático:",
          googleTokenReceived.substring(0, 100)
        );
        throw new Error(
          "No se pudo obtener el email del token de Google: " +
            decodeError.message
        );
      }
      setUserEmail(email);

      const response = await apiClient.post(
        `/attendance/${eventId}/validate-user/`,
        {
          token,
          email,
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Backend response completa:', JSON.stringify(data, null, 2));
        console.log('🔍 user_type:', data.user_type);
        console.log('🔍 is_new_user:', data.is_new_user);
        console.log('🔍 username:', data.username);

        // Compatibilidad con ambas estructuras de respuesta
        const hasSuccess = data.success !== undefined;
        const userType = data.user_type || data.userType;
        const isNewUser = data.is_new_user !== undefined ? data.is_new_user : true;
        
        if ((hasSuccess && data.success) || (!hasSuccess && userType)) {

          if (userType === "internal") {
            await registerInternalUser(email, credentialResponse.credential);
          } else if (userType === "external" || userType.includes("externo")) {
            // Usuario externo - verificar si es nuevo o existente
            if (isNewUser === false) {
              console.log('✅ Usuario externo existente detectado');
              // Usar datos reales del backend si están disponibles
              const existingData = data.existing_data || {
                name: data.username || "Usuario Registrado",
                phone: "Sin teléfono registrado",
                document: "Sin documento registrado",
                organization: "Sin organización registrada"
              };
              setExistingUserData(existingData);
              setGuestInfo({
                name: existingData.name,
                email: email,
                phone: existingData.phone,
                document: existingData.document,
                organization: existingData.organization
              });
              setStep("confirm");
            } else {
              console.log('ℹ️ Usuario externo nuevo - ir a formulario');
              setGuestInfo(prev => ({ ...prev, email }));
              setStep("form");
            }
          }
        } else {
          console.log('❌ Backend response success=false:', data.message);
          if (data.message && data.message.includes("no está invitado")) {
            setError(
              `🚫 ${data.message}\n\n📝 Para registrar tu asistencia:\n1. Verifica que uses el email correcto\n2. Contacta al organizador del evento\n3. Asegúrate de estar en la lista de invitados`
            );
          } else {
            setError(data.message || "Error al validar usuario");
          }
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403) {
          setError(
            `🚫 Acceso denegado: ${"No te han invitado a este evento"}`
          );
        } else if (response.status === 409) {
          setError("Ya registraste tu asistencia para este evento");
        } else {
          setError("Error de conexión con el servidor");
        }
      }
    } catch (err) {
      console.error("Error en handleGoogleLogin:", err);
      setError("Error al procesar login: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const registerInternalUser = async (email, googleTokenParam) => {
    try {
      // Validar timing del evento antes de registrar
      if (eventInfo) {
        const timingValidation = validateEventTimingForRegistration({
          start: eventInfo.start,
          end: eventInfo.end
        });
        if (!timingValidation.valid) {
          setError(timingValidation.message);
          return;
        }
      }

      const rawData = {
        token: String(token),
        email: String(email),
      };

      const requestData = sanitizeRequestData(rawData);

      // Validar datos básicos
      if (!requestData.token || !requestData.email) {
        throw new Error("Token y email son requeridos");
      }

      if (!isValidEventId(eventId)) {
        throw new Error("ID de evento inválido: " + eventId);
      }

      const response = await apiClient.post(
        `/attendance/${eventId}/register-internal/`,
        requestData
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStep("success");
        } else {
          setError(data.message || "Error al registrar asistencia");
        }
      } else {
        const errorData = await response.json().catch(() => ({}));

        if (response.status === 403) {
          setError(
            `🚫 Acceso denegado: ${
              errorData.message ||
              "No tienes permisos para registrar asistencia en este evento"
            }`
          );
        } else if (response.status === 410) {
          setError("⏰ El registro de asistencia ha caducado.\n\nEl período de registro para este evento ha finalizado.");
        } else if (response.status === 425) {
          setError("⏰ El evento aún no ha comenzado.\n\nPodrás registrar tu asistencia cuando el evento haya iniciado.");
        } else {
          setError(
            `Error ${response.status}: ${
              errorData.message ||
              errorData.error ||
              "Error al registrar asistencia"
            }`
          );
        }
      }
    } catch (err) {
      console.error("Error en registerInternalUser:", err);
      setError("Error al registrar asistencia: " + err.message);
    }
  };

  const handleUseExistingData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validar timing del evento antes de registrar
      if (eventInfo) {
        const timingValidation = validateEventTimingForRegistration({
          start: eventInfo.start,
          end: eventInfo.end
        });
        if (!timingValidation.valid) {
          setError(timingValidation.message);
          setLoading(false);
          return;
        }
      }

      const response = await apiClient.post(
        `/attendance/${eventId}/register-external/`,
        {
          token,
          ...guestInfo,
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStep("success");
        } else {
          setError(data.message || "Error al registrar asistencia");
        }
      } else {
        setError("Error al registrar asistencia");
      }
    } catch (err) {
      setError("Error al registrar asistencia: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditData = () => {
    setStep("form");
  };

  const handleExternalUserSubmit = async (e) => {
    e.preventDefault();

    if (!guestInfo.name || !guestInfo.phone) {
      setError("Por favor completa todos los campos obligatorios");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validar timing del evento antes de registrar
      if (eventInfo) {
        const timingValidation = validateEventTimingForRegistration({
          start: eventInfo.start,
          end: eventInfo.end
        });
        if (!timingValidation.valid) {
          setError(timingValidation.message);
          setLoading(false);
          return;
        }
      }

      const response = await apiClient.post(
        `/attendance/${eventId}/register-external/`,
        {
          token,
          ...guestInfo,
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStep("success");
        } else {
          setError(data.message || "Error al registrar asistencia");
        }
      } else {
        setError("Error al registrar asistencia");
      }
    } catch (err) {
      setError("Error al registrar asistencia: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !eventInfo) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <Spinner animation="border" className="mb-3" />
            <p>Cargando información del evento...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error && !eventInfo) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Alert variant="danger">
              <h5>Error</h5>
              <p>{error}</p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  if (step === "success") {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card className="text-center">
              <Card.Body className="py-5">
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  size="3x"
                  className="text-success mb-3"
                />
                <h3 className="text-success mb-3">¡Asistencia Registrada!</h3>
                <p className="mb-3">
                  Tu asistencia al evento <strong>{eventInfo?.title}</strong> ha
                  sido registrada exitosamente.
                </p>
                <div className="bg-light p-3 rounded mb-3">
                  <small className="text-muted">
                    <strong>Detalles del registro:</strong>
                    <br />
                    Email: {userEmail}
                    <br />
                    Fecha: {new Date().toLocaleString("es-ES")}
                  </small>
                </div>
                <p className="text-muted">
                  Gracias por confirmar tu asistencia.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (step === "login") {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6}>
            <Card>
              <Card.Header className="bg-primary text-white text-center">
                <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                <h4 className="mb-0">Registro de Asistencia</h4>
              </Card.Header>
              <Card.Body className="text-center py-5">
                {eventInfo && (
                  <div className="mb-4">
                    <h5 className="mb-3">{eventInfo.title}</h5>
                    <p className="text-muted">
                      <strong>Fecha:</strong> {eventInfo.date} -{" "}
                      <strong>Hora:</strong> {eventInfo.time}
                    </p>
                  </div>
                )}

                <div className="mb-4">
                  <FontAwesomeIcon
                    icon={faSignInAlt}
                    size="3x"
                    className="text-primary mb-3"
                  />
                  <h5>Te damos la bienvenida como persona invitada.</h5>
                  <p className="text-muted">
                    Para registrar tu asistencia, por favor inicia sesión con el
                    correo electrónico al que te enviaron la invitación.
                  </p>
                </div>

                {error && (
                  <Alert
                    variant={error.includes("🚫") ? "warning" : "danger"}
                    className="mb-3"
                  >
                    <div style={{ whiteSpace: "pre-line" }}>{error}</div>
                  </Alert>
                )}

                <div className="d-flex justify-content-center">
                  {loading ? (
                    <Spinner animation="border" />
                  ) : (
                    <div style={{ transform: "scale(1.1)" }}>
                      <GoogleLogin
                        onSuccess={handleGoogleLogin}
                        onError={() =>
                          setError("Error al iniciar sesión con Google")
                        }
                        text="signin_with"
                        shape="rectangular"
                        size="large"
                      />
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (step === "confirm") {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <Card.Header className="bg-info text-white text-center">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                <h4 className="mb-0">¡Hola de nuevo!</h4>
              </Card.Header>
              <Card.Body>
                <div className="mb-4 text-center">
                  <p className="text-muted">
                    Hemos encontrado tus datos registrados previamente en nuestro sistema.
                  </p>
                </div>

                <div className="bg-light p-3 rounded mb-4">
                  <h6 className="mb-3">Tus datos registrados:</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Nombre:</strong><br/>{existingUserData?.name || 'No registrado'}</p>
                      <p><strong>Email:</strong><br/>{userEmail}</p>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Teléfono:</strong><br/>{existingUserData?.phone || 'No registrado'}</p>
                      <p><strong>Documento:</strong><br/>{existingUserData?.document || 'No registrado'}</p>
                    </div>
                  </div>
                  {existingUserData?.organization && (
                    <p><strong>Organización:</strong><br/>{existingUserData.organization}</p>
                  )}
                </div>

                <div className="text-center mb-3">
                  <h6>¿Qué deseas hacer?</h6>
                  <p className="text-muted">Puedes usar estos datos o editarlos si han cambiado.</p>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    onClick={handleUseExistingData}
                    disabled={loading}
                    size="lg"
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Registrando...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
                        Usar estos datos y registrar asistencia
                      </>
                    )}
                  </Button>
                  
                  <Button
                    variant="outline-primary"
                    onClick={handleEditData}
                    disabled={loading}
                    size="lg"
                  >
                    <FontAwesomeIcon icon={faUser} className="me-2" />
                    Editar mis datos
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  if (step === "form") {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <Card.Header className="bg-info text-white text-center">
                <FontAwesomeIcon icon={faUser} className="me-2" />
                <h4 className="mb-0">Información del Invitado Externo</h4>
              </Card.Header>
              <Card.Body>
                <div className="mb-4 text-center">
                  <p className="text-muted">
                    Hola <strong>{userEmail}</strong>, por favor completa tu
                    información para registrar tu asistencia.
                  </p>
                </div>

                <Form onSubmit={handleExternalUserSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      Nombre Completo *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={guestInfo.name}
                      onChange={handleInputChange}
                      placeholder="Ingresa tu nombre completo"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Teléfono *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={guestInfo.phone}
                      onChange={handleInputChange}
                      placeholder="Número de teléfono"
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Documento de Identidad</Form.Label>
                    <Form.Control
                      type="text"
                      name="document"
                      value={guestInfo.document}
                      onChange={handleInputChange}
                      placeholder="Número de documento"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Organización</Form.Label>
                    <Form.Control
                      type="text"
                      name="organization"
                      value={guestInfo.organization}
                      onChange={handleInputChange}
                      placeholder="Empresa u organización"
                    />
                  </Form.Group>

                  {error && (
                    <Alert variant="danger" className="mb-3">
                      {error}
                    </Alert>
                  )}

                  <div className="d-grid">
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={loading}
                      size="lg"
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Registrando...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon
                            icon={faCalendarCheck}
                            className="me-2"
                          />
                          Confirmar Asistencia
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
};

export default AttendancePage;
